import { useEffect, useState } from 'react';

import { differenceBy, pick } from 'lodash-es';
import { useNavigate } from 'react-router-dom';

import { ICartEntry, IItem } from '@rbi-ctg/menu';
import { IAddOnSection, IAddOnSectionOption } from 'hooks/use-feature-menu-add-on';
import { useMenuOptionsContext } from 'state/menu-options';
import { useOrderContext } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import { routes } from 'utils/routing';
import {
  convertModActionsToSelections,
  getSelectionsFromMenuData,
  transformMenuObjectToCartItem,
} from 'utils/wizard';

export type AddOnSelections = Record<
  string,
  { quantity: number; data: IItem; sectionId: string; cartId?: string }
>;
export type { IAddOnSection, IAddOnSectionOption };

interface IUseCartAddOnsOptions {
  initialSelections?: AddOnSelections;
  addOnData?: IAddOnSection[];
}

const useCartAddOns = (options?: IUseCartAddOnsOptions) => {
  const { initialSelections = {}, addOnData = [] } = options ?? {};
  const { serviceMode } = useServiceModeContext();
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [addOnSelections, setAddOnSelections] = useState<AddOnSelections>(initialSelections);
  const [previousSelections, setPreviousSelections] = useState<AddOnSelections>({});
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [totalQuantity, setTotalQuantity] = useState<number>(0);
  const navigate = useNavigate();
  const {
    priceForItemOptionModifier,
    priceForComboSlotSelection,
    pricingFunction,
  } = useMenuOptionsContext();

  const { upsertCart, cartEntries, removeAllFromCart } = useOrderContext();

  // NOTE: Currently this only handles adding IItem menu item type as that is all addOns currently support in Sanity
  const handleAddToCart = () => {
    // just navigate to the cart nothing was selected. Treat this as if they exited the modal
    if (totalQuantity === 0) {
      navigate(routes.cart);
    }

    // We have to figure out which AddOns were there but no longer have quantity
    const entryKeysToRemove = differenceBy(
      Object.keys(previousSelections),
      Object.keys(addOnSelections)
    );

    const entriesToRemove = entryKeysToRemove.map(key => ({
      cartId: previousSelections[key].cartId,
    }));

    const newCartEntries: ICartEntry[] = Object.keys(addOnSelections).reduce(
      (entries: ICartEntry[], selectionKey) => {
        const { quantity, data, cartId: existingCartId } = addOnSelections[selectionKey];
        // Make sure to always get the default modifiers for the item
        const { modifierSelections: modSelectionActions } = getSelectionsFromMenuData({
          data,
          serviceMode,
        });
        // Convert the returned action into wizard state
        const modifierSelections = modSelectionActions
          ? convertModActionsToSelections(modSelectionActions)
          : [];

        // Make sure to always include modifiers that are injected by default by passing it through the same function wizard uses
        const { mainEntry } = transformMenuObjectToCartItem(
          data,
          priceForItemOptionModifier,
          pricingFunction,
          priceForComboSlotSelection,
          { quantity, modifierSelections, existingCartId }
        );
        if (mainEntry) {
          entries.push({
            ...mainEntry,
            isExtra: true,
          });
        }
        return entries;
      },
      []
    );

    removeAllFromCart(entriesToRemove);
    upsertCart(newCartEntries);
    navigate(routes.cart);
  };

  // If the new quantity is zero remove that options itemId from the selections completely
  // Otherwise update that key to the new quantity/data
  const updateQuantityForItem = (
    sectionOption: IAddOnSectionOption,
    quantity: number,
    section: IAddOnSection
  ) => {
    const optionId = sectionOption._key;
    const data = sectionOption.option;
    const sectionId = section._id;

    setAddOnSelections(currentSelections =>
      quantity === 0
        ? pick(
            currentSelections,
            Object.keys(currentSelections).filter(key => key !== optionId)
          )
        : {
            ...currentSelections,
            [optionId]: {
              quantity,
              data,
              sectionId,
              cartId: currentSelections[optionId] && currentSelections[optionId].cartId,
            },
          }
    );
  };

  // Recalculate the total anytime the selections change.
  useEffect(() => {
    const { calculatedPrice: newPrice, calculatedQuantity: newQuantity } = Object.keys(
      addOnSelections
    ).reduce(
      ({ calculatedPrice, calculatedQuantity }, selectionKey) => {
        const { quantity, data } = addOnSelections[selectionKey];
        return {
          calculatedPrice: calculatedPrice + pricingFunction(data, quantity),
          calculatedQuantity: calculatedQuantity + quantity,
        };
      },
      { calculatedPrice: 0, calculatedQuantity: 0 }
    );
    setTotalPrice(newPrice);
    setTotalQuantity(newQuantity);
  }, [addOnSelections, pricingFunction]);

  // Preload selections when we mount.
  useEffect(() => {
    // if no addOnData or cartEntries don't do anything
    if (!addOnData.length || !cartEntries.length) {
      return;
    }

    // get an object of all extra entries with keys being the items id
    const cartEntrySelections = cartEntries.reduce(
      (selections: Record<string, ICartEntry>, entry: ICartEntry) => {
        if (entry.isExtra) {
          return { ...selections, [entry._id]: entry };
        }
        return selections;
      },
      {}
    );

    if (!Object.keys(cartEntrySelections).length) {
      return;
    }

    setIsEdit(true);

    const preloadedSelections = addOnData.reduce((selections: AddOnSelections, section) => {
      let sectionSelections = {};
      section.options.forEach(({ _key: selectionKey, option: item }) => {
        if (cartEntrySelections[item._id]) {
          sectionSelections = {
            ...sectionSelections,
            [selectionKey]: {
              quantity: cartEntrySelections[item._id].quantity,
              data: item,
              sectionId: section._id,
              cartId: cartEntrySelections[item._id].cartId,
            },
          };
        }
      });
      return { ...selections, ...sectionSelections };
    }, {});

    setPreviousSelections(preloadedSelections); // hold onto the selections we load with before the guest makes changes
    setAddOnSelections(preloadedSelections);
  }, [addOnData, cartEntries]);

  return {
    handleAddToCart,
    isEdit,
    updateQuantityForItem,
    addOnSelections,
    totalPrice,
    totalQuantity,
  };
};

export default useCartAddOns;
